// frontend/src/constants/metricIcons.ts
import {
  // MUSIC & PERFORMANCE
  MusicNote,
  Album,
  AudioFile,
  Headphones,
  Piano,
  Mic,
  GraphicEq,
  QueueMusic,
  LibraryMusic,
  Radio,
  Audiotrack,
  MusicVideo,
  Speaker,
  VolumeUp,
  Theaters,
  TheaterComedy,
  LocalActivity,
  ConfirmationNumber,
  Stadium,
  
  // PEOPLE & AUDIENCE
  People,
  Groups,
  Group,
  Person,
  PersonAdd,
  Diversity1,
  Diversity2,
  Diversity3,
  EmojiPeople,
  Celebration,
  FamilyRestroom,
  ChildCare,
  Elderly,
  AccessibleForward,
  School,
  Face,
  SentimentVerySatisfied,
  
  // IMPACT & COMMUNITY
  VolunteerActivism,
  Favorite,
  FavoriteBorder,
  Public,
  Language,
  LocationCity,
  Apartment,
  HomeWork,
  Business,
  Store,
  AccountBalance,
  Gavel,
  Handshake,
  ThumbUp,
  Stars,
  EmojiEvents,
  LocalFireDepartment,
  
  // ARTS & CULTURE
  Palette,
  Brush,
  Draw,
  AutoAwesome,
  AutoStories,
  LocalLibrary,
  MenuBook,
  ImportContacts,
  Newspaper,
  Article,
  Create,
  Edit,
  Camera,
  PhotoCamera,
  Videocam,
  Movie,
  
  // EDUCATION & LEARNING
  CastForEducation,
  WorkspacePremium,
  Psychology,
  Science,
  Lightbulb,
  TipsAndUpdates,
  ModelTraining,
  RecordVoiceOver,
  HearingDisabled,
  Hearing,
  
  // FINANCIAL
  Euro,
  AttachMoney,
  AccountBalanceWallet,
  Payments,
  MonetizationOn,
  CurrencyExchange,
  Savings,
  PriceCheck,
  TrendingUp,
  TrendingDown,
  ShowChart,
  BarChart,
  PieChart,
  Assessment,
  Insights,
  Analytics,
  
  // GROWTH & PERFORMANCE
  Rocket,
  Speed,
  Timeline,
  MultilineChart,
  Equalizer,
  SignalCellularAlt,
  ArrowUpward,
  ArrowForward,
  North,
  NorthEast,
  Upgrade,
  
  // TIME & EVENTS
  Schedule,
  Event,
  CalendarToday,
  DateRange,
  Today,
  Update,
  History,
  AccessTime,
  Timer,
  Alarm,
  EventAvailable,
  EventNote,
  
  // REACH & DISTRIBUTION
  Share,
  Campaign,
  SpatialAudioOff,
  SpatialAudio,
  Podcasts,
  Wifi,
  NetworkCheck,
  SignalCellular4Bar,
  PublicOff,
  
  // SUSTAINABILITY & NATURE
  Nature,
  Park,
  Forest,
  Spa,
  FilterVintage,
  WbSunny,
  NightsStay,
  Recycling,
  
  // IRISH & LOCAL
  Flag,
  LocationOn,
  Place,
  PinDrop,
  MyLocation,
  Map,
  Explore,
  TravelExplore,
  
  // AWARDS & RECOGNITION
  MilitaryTech,
  CardGiftcard,
  Redeem,
  LocalOffer,
  Grade,
  Star,
  StarBorder,
  StarHalf,
  
  // ACCESSIBILITY & INCLUSION
  Accessible,
  ElderlyWoman,
  PregnantWoman,
  ReduceCapacity,
  SafetyDivider,
  SignLanguage,
  
  // COLLABORATION & PARTNERSHIP
  ConnectWithoutContact,
  Hub,
  AccountTree,
  Forum,
  QuestionAnswer,
  Support,
  VerifiedUser,
  
  // INNOVATION & CREATIVITY
  Flare,
  Bolt,
  Brightness7,
  WbIncandescent,
  BubbleChart,
  ScatterPlot,
  Functions,
  
  // MEDIA & BROADCAST
  LiveTv,
  OndemandVideo,
  Stream,
  Cast,
  PlayCircle,
  FiberManualRecord,
  
  // ENGAGEMENT
  TouchApp,
  PanTool,
  BackHand,
  Visibility,
  RemoveRedEye,
  
  // QUALITY & EXCELLENCE
  VerifiedUser as Verified,
  CheckCircle,
  Verified as VerifiedBadge,
  WorkspacePremium as Premium,
  Diamond,
  AutoAwesome as Sparkles
} from '@mui/icons-material';

export interface MetricIcon {
  value: string;
  label: string;
  icon: React.ComponentType;
  category: string;
}

export const METRIC_ICONS: MetricIcon[] = [
  // =====================================================
  // MUSIC & PERFORMANCE (Primary Category)
  // =====================================================
  { value: 'music_note', label: 'Music Note', icon: MusicNote, category: 'music' },
  { value: 'album', label: 'Album / Record', icon: Album, category: 'music' },
  { value: 'piano', label: 'Piano', icon: Piano, category: 'music' },
  { value: 'mic', label: 'Microphone', icon: Mic, category: 'music' },
  { value: 'headphones', label: 'Headphones', icon: Headphones, category: 'music' },
  { value: 'graphic_eq', label: 'Sound Waves / EQ', icon: GraphicEq, category: 'music' },
  { value: 'queue_music', label: 'Music Queue', icon: QueueMusic, category: 'music' },
  { value: 'library_music', label: 'Music Library', icon: LibraryMusic, category: 'music' },
  { value: 'audiotrack', label: 'Audio Track', icon: Audiotrack, category: 'music' },
  { value: 'music_video', label: 'Music Video', icon: MusicVideo, category: 'music' },
  { value: 'radio', label: 'Radio', icon: Radio, category: 'music' },
  { value: 'speaker', label: 'Speaker', icon: Speaker, category: 'music' },
  { value: 'volume_up', label: 'Volume / Sound', icon: VolumeUp, category: 'music' },
  { value: 'audio_file', label: 'Audio File', icon: AudioFile, category: 'music' },
  
  // Performance Venues & Shows
  { value: 'theaters', label: 'Theater / Venue', icon: Theaters, category: 'music' },
  { value: 'theater_comedy', label: 'Performance / Stage', icon: TheaterComedy, category: 'music' },
  { value: 'local_activity', label: 'Ticket / Activity', icon: LocalActivity, category: 'music' },
  { value: 'confirmation_number', label: 'Admission Ticket', icon: ConfirmationNumber, category: 'music' },
  { value: 'stadium', label: 'Stadium / Large Venue', icon: Stadium, category: 'music' },
  
  // =====================================================
  // PEOPLE & AUDIENCE (Critical for Impact)
  // =====================================================
  { value: 'people', label: 'People / Audience', icon: People, category: 'people' },
  { value: 'groups', label: 'Groups / Community', icon: Groups, category: 'people' },
  { value: 'group', label: 'Group', icon: Group, category: 'people' },
  { value: 'person', label: 'Person / Individual', icon: Person, category: 'people' },
  { value: 'person_add', label: 'Add Person / Growth', icon: PersonAdd, category: 'people' },
  { value: 'diversity_1', label: 'Diversity', icon: Diversity1, category: 'people' },
  { value: 'diversity_2', label: 'Diverse Group', icon: Diversity2, category: 'people' },
  { value: 'diversity_3', label: 'Inclusive Community', icon: Diversity3, category: 'people' },
  { value: 'emoji_people', label: 'Happy Person', icon: EmojiPeople, category: 'people' },
  { value: 'celebration', label: 'Celebration', icon: Celebration, category: 'people' },
  { value: 'family_restroom', label: 'Family', icon: FamilyRestroom, category: 'people' },
  { value: 'child_care', label: 'Children / Youth', icon: ChildCare, category: 'people' },
  { value: 'elderly', label: 'Elderly / Seniors', icon: Elderly, category: 'people' },
  { value: 'face', label: 'Face / Identity', icon: Face, category: 'people' },
  { value: 'sentiment_satisfied', label: 'Satisfaction / Happy', icon: SentimentVerySatisfied, category: 'people' },
  
  // Accessibility & Inclusion
  { value: 'accessible_forward', label: 'Accessibility', icon: AccessibleForward, category: 'people' },
  { value: 'accessible', label: 'Accessible', icon: Accessible, category: 'people' },
  { value: 'elderly_woman', label: 'Elderly Woman', icon: ElderlyWoman, category: 'people' },
  { value: 'pregnant_woman', label: 'Pregnant Woman', icon: PregnantWoman, category: 'people' },
  { value: 'sign_language', label: 'Sign Language', icon: SignLanguage, category: 'people' },
  { value: 'hearing', label: 'Hearing', icon: Hearing, category: 'people' },
  { value: 'hearing_disabled', label: 'Hearing Impaired', icon: HearingDisabled, category: 'people' },
  
  // =====================================================
  // IMPACT & COMMUNITY
  // =====================================================
  { value: 'volunteer_activism', label: 'Volunteering / Activism', icon: VolunteerActivism, category: 'impact' },
  { value: 'favorite', label: 'Love / Support', icon: Favorite, category: 'impact' },
  { value: 'favorite_border', label: 'Like / Appreciate', icon: FavoriteBorder, category: 'impact' },
  { value: 'public', label: 'Public / Global Reach', icon: Public, category: 'impact' },
  { value: 'language', label: 'International / Languages', icon: Language, category: 'impact' },
  { value: 'location_city', label: 'City / Urban', icon: LocationCity, category: 'impact' },
  { value: 'apartment', label: 'Community Buildings', icon: Apartment, category: 'impact' },
  { value: 'home_work', label: 'Local Community', icon: HomeWork, category: 'impact' },
  { value: 'business', label: 'Business / Organization', icon: Business, category: 'impact' },
  { value: 'store', label: 'Local Store / Venue', icon: Store, category: 'impact' },
  { value: 'account_balance', label: 'Institution / Government', icon: AccountBalance, category: 'impact' },
  { value: 'handshake', label: 'Partnership', icon: Handshake, category: 'impact' },
  { value: 'thumb_up', label: 'Approval / Positive', icon: ThumbUp, category: 'impact' },
  { value: 'stars', label: 'Excellence / Quality', icon: Stars, category: 'impact' },
  { value: 'emoji_events', label: 'Achievement / Event', icon: EmojiEvents, category: 'impact' },
  { value: 'local_fire_department', label: 'Passion / Energy', icon: LocalFireDepartment, category: 'impact' },
  
  // Collaboration
  { value: 'connect_without_contact', label: 'Connection', icon: ConnectWithoutContact, category: 'impact' },
  { value: 'hub', label: 'Hub / Network', icon: Hub, category: 'impact' },
  { value: 'account_tree', label: 'Organization / Structure', icon: AccountTree, category: 'impact' },
  { value: 'forum', label: 'Discussion / Forum', icon: Forum, category: 'impact' },
  { value: 'support', label: 'Support', icon: Support, category: 'impact' },
  
  // =====================================================
  // ARTS & CULTURE
  // =====================================================
  { value: 'palette', label: 'Art / Creativity', icon: Palette, category: 'arts' },
  { value: 'brush', label: 'Painting / Visual Arts', icon: Brush, category: 'arts' },
  { value: 'draw', label: 'Drawing / Design', icon: Draw, category: 'arts' },
  { value: 'auto_awesome', label: 'Creative / Magical', icon: AutoAwesome, category: 'arts' },
  { value: 'auto_stories', label: 'Stories / Narrative', icon: AutoStories, category: 'arts' },
  { value: 'local_library', label: 'Library / Archive', icon: LocalLibrary, category: 'arts' },
  { value: 'menu_book', label: 'Book / Publication', icon: MenuBook, category: 'arts' },
  { value: 'newspaper', label: 'News / Press', icon: Newspaper, category: 'arts' },
  { value: 'article', label: 'Article / Content', icon: Article, category: 'arts' },
  { value: 'create', label: 'Create / Compose', icon: Create, category: 'arts' },
  { value: 'camera', label: 'Photography', icon: Camera, category: 'arts' },
  { value: 'photo_camera', label: 'Camera', icon: PhotoCamera, category: 'arts' },
  { value: 'videocam', label: 'Video Production', icon: Videocam, category: 'arts' },
  { value: 'movie', label: 'Film / Movie', icon: Movie, category: 'arts' },
  
  // =====================================================
  // EDUCATION & LEARNING
  // =====================================================
  { value: 'school', label: 'Education / School', icon: School, category: 'education' },
  { value: 'cast_for_education', label: 'Teaching / Training', icon: CastForEducation, category: 'education' },
  { value: 'workspace_premium', label: 'Premium Education', icon: WorkspacePremium, category: 'education' },
  { value: 'psychology', label: 'Learning / Psychology', icon: Psychology, category: 'education' },
  { value: 'science', label: 'Science / Research', icon: Science, category: 'education' },
  { value: 'lightbulb', label: 'Ideas / Innovation', icon: Lightbulb, category: 'education' },
  { value: 'tips_and_updates', label: 'Tips / Insights', icon: TipsAndUpdates, category: 'education' },
  { value: 'model_training', label: 'Training / Development', icon: ModelTraining, category: 'education' },
  { value: 'record_voice_over', label: 'Speaking / Voice', icon: RecordVoiceOver, category: 'education' },
  
  // =====================================================
  // FINANCIAL
  // =====================================================
  { value: 'euro', label: 'Euro / Money', icon: Euro, category: 'financial' },
  { value: 'attach_money', label: 'Money / Funding', icon: AttachMoney, category: 'financial' },
  { value: 'account_balance_wallet', label: 'Wallet / Budget', icon: AccountBalanceWallet, category: 'financial' },
  { value: 'payments', label: 'Payments', icon: Payments, category: 'financial' },
  { value: 'monetization_on', label: 'Revenue / Income', icon: MonetizationOn, category: 'financial' },
  { value: 'currency_exchange', label: 'Exchange / Trade', icon: CurrencyExchange, category: 'financial' },
  { value: 'savings', label: 'Savings / Reserve', icon: Savings, category: 'financial' },
  { value: 'price_check', label: 'Price / Cost', icon: PriceCheck, category: 'financial' },
  
  // =====================================================
  // GROWTH & PERFORMANCE
  // =====================================================
  { value: 'trending_up', label: 'Growth / Trending Up', icon: TrendingUp, category: 'performance' },
  { value: 'trending_down', label: 'Decline / Trending Down', icon: TrendingDown, category: 'performance' },
  { value: 'show_chart', label: 'Chart / Metrics', icon: ShowChart, category: 'performance' },
  { value: 'bar_chart', label: 'Bar Chart', icon: BarChart, category: 'performance' },
  { value: 'pie_chart', label: 'Pie Chart', icon: PieChart, category: 'performance' },
  { value: 'assessment', label: 'Assessment / Analysis', icon: Assessment, category: 'performance' },
  { value: 'insights', label: 'Insights / Data', icon: Insights, category: 'performance' },
  { value: 'analytics', label: 'Analytics', icon: Analytics, category: 'performance' },
  { value: 'rocket', label: 'Rapid Growth', icon: Rocket, category: 'performance' },
  { value: 'speed', label: 'Speed / Fast', icon: Speed, category: 'performance' },
  { value: 'timeline', label: 'Timeline / Progress', icon: Timeline, category: 'performance' },
  { value: 'multiline_chart', label: 'Multi-line Chart', icon: MultilineChart, category: 'performance' },
  { value: 'equalizer', label: 'Equalizer / Levels', icon: Equalizer, category: 'performance' },
  { value: 'arrow_upward', label: 'Upward Arrow', icon: ArrowUpward, category: 'performance' },
  { value: 'arrow_forward', label: 'Forward Arrow', icon: ArrowForward, category: 'performance' },
  { value: 'north_east', label: 'Growth Direction', icon: NorthEast, category: 'performance' },
  { value: 'upgrade', label: 'Upgrade / Improvement', icon: Upgrade, category: 'performance' },
  
  // =====================================================
  // TIME & EVENTS
  // =====================================================
  { value: 'schedule', label: 'Schedule / Time', icon: Schedule, category: 'time' },
  { value: 'event', label: 'Event / Calendar', icon: Event, category: 'time' },
  { value: 'calendar_today', label: 'Today / Current', icon: CalendarToday, category: 'time' },
  { value: 'date_range', label: 'Date Range', icon: DateRange, category: 'time' },
  { value: 'today', label: 'Today', icon: Today, category: 'time' },
  { value: 'update', label: 'Update / Recent', icon: Update, category: 'time' },
  { value: 'history', label: 'History / Past', icon: History, category: 'time' },
  { value: 'access_time', label: 'Time / Duration', icon: AccessTime, category: 'time' },
  { value: 'timer', label: 'Timer / Countdown', icon: Timer, category: 'time' },
  { value: 'event_available', label: 'Available / Open', icon: EventAvailable, category: 'time' },
  { value: 'event_note', label: 'Event Notes', icon: EventNote, category: 'time' },
  
  // =====================================================
  // REACH & DISTRIBUTION
  // =====================================================
  { value: 'share', label: 'Share / Distribution', icon: Share, category: 'reach' },
  { value: 'campaign', label: 'Campaign / Marketing', icon: Campaign, category: 'reach' },
  { value: 'podcasts', label: 'Podcasts', icon: Podcasts, category: 'reach' },
  { value: 'wifi', label: 'WiFi / Connection', icon: Wifi, category: 'reach' },
  { value: 'live_tv', label: 'Live TV', icon: LiveTv, category: 'reach' },
  { value: 'stream', label: 'Stream / Live', icon: Stream, category: 'reach' },
  { value: 'visibility', label: 'Visibility / Views', icon: Visibility, category: 'reach' },
  { value: 'remove_red_eye', label: 'Eye / Watching', icon: RemoveRedEye, category: 'reach' },
  
  // =====================================================
  // SUSTAINABILITY & NATURE (Irish Context)
  // =====================================================
  { value: 'nature', label: 'Nature / Environment', icon: Nature, category: 'sustainability' },
  { value: 'park', label: 'Park / Outdoor', icon: Park, category: 'sustainability' },
  { value: 'forest', label: 'Forest / Trees', icon: Forest, category: 'sustainability' },
  { value: 'spa', label: 'Spa / Wellness', icon: Spa, category: 'sustainability' },
  { value: 'wb_sunny', label: 'Sunny / Bright', icon: WbSunny, category: 'sustainability' },
  { value: 'recycling', label: 'Recycling / Circular', icon: Recycling, category: 'sustainability' },
  
  // =====================================================
  // IRISH & LOCAL CONTEXT
  // =====================================================
  { value: 'flag', label: 'Flag / National', icon: Flag, category: 'local' },
  { value: 'location_on', label: 'Location / Place', icon: LocationOn, category: 'local' },
  { value: 'place', label: 'Place / Venue', icon: Place, category: 'local' },
  { value: 'pin_drop', label: 'Pin / Specific Location', icon: PinDrop, category: 'local' },
  { value: 'my_location', label: 'My Location / Local', icon: MyLocation, category: 'local' },
  { value: 'map', label: 'Map / Geography', icon: Map, category: 'local' },
  { value: 'explore', label: 'Explore / Discover', icon: Explore, category: 'local' },
  { value: 'travel_explore', label: 'Travel / Tourism', icon: TravelExplore, category: 'local' },
  
  // =====================================================
  // AWARDS & RECOGNITION
  // =====================================================
  { value: 'military_tech', label: 'Award / Medal', icon: MilitaryTech, category: 'awards' },
  { value: 'card_giftcard', label: 'Gift / Grant', icon: CardGiftcard, category: 'awards' },
  { value: 'redeem', label: 'Redeem / Value', icon: Redeem, category: 'awards' },
  { value: 'grade', label: 'Grade / Rating', icon: Grade, category: 'awards' },
  { value: 'star', label: 'Star / Featured', icon: Star, category: 'awards' },
  { value: 'star_border', label: 'Star Outline', icon: StarBorder, category: 'awards' },
  { value: 'star_half', label: 'Half Star', icon: StarHalf, category: 'awards' },
  
  // =====================================================
  // QUALITY & EXCELLENCE
  // =====================================================
  { value: 'verified', label: 'Verified / Trusted', icon: Verified, category: 'quality' },
  { value: 'check_circle', label: 'Check / Complete', icon: CheckCircle, category: 'quality' },
  { value: 'verified_badge', label: 'Badge / Certification', icon: VerifiedBadge, category: 'quality' },
  { value: 'premium', label: 'Premium / High Quality', icon: Premium, category: 'quality' },
  { value: 'diamond', label: 'Diamond / Exceptional', icon: Diamond, category: 'quality' },
  { value: 'sparkles', label: 'Sparkles / Magic', icon: Sparkles, category: 'quality' },
  
  // =====================================================
  // ENGAGEMENT & INTERACTION
  // =====================================================
  { value: 'touch_app', label: 'Touch / Interact', icon: TouchApp, category: 'engagement' },
  { value: 'pan_tool', label: 'Hand / Interaction', icon: PanTool, category: 'engagement' },
  { value: 'back_hand', label: 'Stop / Attention', icon: BackHand, category: 'engagement' },
];

// Helper function to get icons by category
export const getIconsByCategory = (category: string): MetricIcon[] => {
  return METRIC_ICONS.filter(icon => icon.category === category);
};

// Helper function to get all categories
export const getCategories = (): string[] => {
  const categories = new Set(METRIC_ICONS.map(icon => icon.category));
  return Array.from(categories);
};

// Helper function to find icon by value
export const getIconByValue = (value: string): MetricIcon | undefined => {
  return METRIC_ICONS.find(icon => icon.value === value);
};

// Category labels for display
export const CATEGORY_LABELS: Record<string, string> = {
  music: '🎵 Music & Performance',
  people: '👥 People & Audience',
  impact: '💫 Impact & Community',
  arts: '🎨 Arts & Culture',
  education: '📚 Education & Learning',
  financial: '💰 Financial',
  performance: '📈 Growth & Performance',
  time: '⏰ Time & Events',
  reach: '📡 Reach & Distribution',
  sustainability: '🌿 Sustainability',
  local: '🇮🇪 Irish & Local',
  awards: '🏆 Awards & Recognition',
  quality: '✨ Quality & Excellence',
  engagement: '🤝 Engagement'
};